import PropTypes from 'prop-types';
import { memo, useState, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
//
import { navVerticalConfig } from '../config';
import { StyledSubheader } from './styles';

import NavList from './nav-list';
import { useOrganizationContext } from 'src/context/organization';
import { isLockExemptPath } from 'src/utils/organization-helper';
import { ArrowDropDown, ArrowDropUp, ArrowRight } from '@mui/icons-material';

// ----------------------------------------------------------------------

/**
 * How a locked entry reads: greyed out and inert, same as the white veil that used to cover the
 * whole nav. It is applied per entry rather than once over the Stack precisely so Billing can
 * stay live inside it — an overlay has no way to leave a hole.
 *
 * This is presentation, not access control: nothing here stops a typed URL, and nothing needs
 * to — an expired org that reaches a locked route still meets the card in layouts/dashboard/main.
 */
const LOCKED_SX = { opacity: 0.35, pointerEvents: 'none', userSelect: 'none' };

/** True when this entry, or anything nested under it, is a screen the lock leaves open. */
const isLockExemptItem = (item) =>
  isLockExemptPath(item?.path) || (item?.children ?? []).some(isLockExemptItem);

function NavSectionVertical({ data, config, sx, ...other }) {
  const { isOrgActive } = useOrganizationContext();
  // An expired or deactivated org keeps exactly one route out — Billing. See isLockExemptPath.
  const locked = !isOrgActive;

  return (
    <Stack sx={{ ...sx, position: 'relative' }} {...other}>
      {data.map((group, index) => {
        const hasExemptItem = (group.items ?? []).some(isLockExemptItem);
        return (
          <Group
            key={group.subheader || index}
            subheader={group.subheader}
            items={group.items}
            config={navVerticalConfig(config)}
            // A collapsed group must never be able to hide the one item still reachable:
            // ORGANIZATION SETTINGS starts closed for managers, and its Billing child is the
            // whole point of the exemption.
            initiallyClosed={locked && hasExemptItem ? false : group?.initiallyClosed}
            locked={locked}
          />
        );
      })}
    </Stack>
  );
}

NavSectionVertical.propTypes = {
  config: PropTypes.object,
  data: PropTypes.array,
  sx: PropTypes.object,
};

export default memo(NavSectionVertical);

// ----------------------------------------------------------------------

function Group({ subheader, items, config, initiallyClosed = false, locked = false }) {
  const [open, setOpen] = useState(!initiallyClosed);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  // Only the exempt entries survive the lock. The group's own subheader survives with them,
  // otherwise the toggle that reveals them would be dead.
  const hasExemptItem = (items ?? []).some(isLockExemptItem);

  const renderContent = items?.map((list) => (
    <Box
      key={list.title + list.path}
      sx={locked && !isLockExemptItem(list) ? LOCKED_SX : undefined}
    >
      <NavList data={list} depth={1} hasChild={!!list.children} config={config} />
    </Box>
  ));

  return (
    <List disablePadding sx={{ px: 2 }}>
      {subheader ? (
        <>
          <StyledSubheader
            disableGutters
            disableSticky
            onClick={handleToggle}
            config={config}
            sx={{ width: '100%', ...(locked && !hasExemptItem ? LOCKED_SX : {}) }}
          >
            <Stack
              direction={'row'}
              flex={1}
              width={'100%'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              {subheader}
              {open ? <ArrowDropDown /> : <ArrowRight />}
            </Stack>
          </StyledSubheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </List>
  );
}

Group.propTypes = {
  config: PropTypes.object,
  initiallyClosed: PropTypes.bool,
  items: PropTypes.array,
  locked: PropTypes.bool,
  subheader: PropTypes.string,
};
